import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../lib/auth/useAuth";
import { useWorkerTimeline } from "./useWorkerTimeline";
import { useSessionsOpen } from "./useSessionsOpen";
import { useRecordAttendance } from "./useAttendance";
import { useEndActivity } from "./useActivities";
import { useStartBreak, useEndBreak } from "./useBreaks";
import { useUiStore } from "../state/uiStore";
import {
  buildEvent,
  deriveSessionState,
  getWorkerUrl,
  reconcileSessionState,
} from "../lib/session";
import type { SessionState } from "../lib/session";

export interface UseSessionResult extends SessionState {
  workerUrl: string;
  online: boolean;
  pendingSync: number;
  loading: boolean;
  badge: () => Promise<void>;
  toggleBreak: () => Promise<void>;
  stopActivity: () => Promise<void>;
}

export function useSession(): UseSessionResult {
  const { user } = useAuth();
  const workerUrl = useMemo(() => {
    try {
      return getWorkerUrl(user);
    } catch {
      return "";
    }
  }, [user]);

  const fromIso = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }, []);

  const timeline = useWorkerTimeline(
    { worker_url: workerUrl, from: fromIso, limit: 200 },
    { enabled: !!workerUrl },
  );
  const sessionsOpen = useSessionsOpen();
  const recordAttendance = useRecordAttendance();
  const endActivity = useEndActivity();
  const startBreak = useStartBreak();
  const endBreak = useEndBreak();
  const pushToast = useUiStore((s) => s.pushToast);

  const myOpenSession = useMemo(
    () =>
      (sessionsOpen.data?.sessions ?? []).find(
        (s) => s.worker_url === workerUrl,
      ),
    [sessionsOpen.data?.sessions, workerUrl],
  );

  const state = useMemo(
    () =>
      reconcileSessionState(
        deriveSessionState(timeline.data?.events ?? []),
        myOpenSession,
      ),
    [timeline.data?.events, myOpenSession],
  );

  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  async function badge(): Promise<void> {
    if (!workerUrl) return;
    try {
      const ev = await buildEvent(
        workerUrl,
        state.attendanceOpen ? "attendance_out" : "attendance_in",
      );
      await recordAttendance.mutateAsync(ev);
      pushToast({
        variant: state.attendanceOpen ? "out" : "in",
        title: state.attendanceOpen
          ? "Badge sortie enregistré"
          : "Badge entrée enregistré",
        subtitle: online ? "Synchronisé" : "Stocké localement",
      });
    } catch {
      pushToast({
        variant: "error",
        title: "Erreur badge",
        subtitle: "Impossible d'enregistrer le pointage.",
      });
    }
  }

  async function toggleBreak(): Promise<void> {
    if (!workerUrl) return;
    try {
      if (state.onBreak) {
        const ev = await buildEvent(workerUrl, "break_end");
        await endBreak.mutateAsync(ev);
        pushToast({ variant: "in", title: "Pause terminée" });
      } else {
        const ev = await buildEvent(workerUrl, "break_start");
        await startBreak.mutateAsync(ev);
        pushToast({
          variant: "pause",
          title: "Pause démarrée",
          subtitle: "L'activité reste ouverte",
        });
      }
    } catch {
      pushToast({
        variant: "error",
        title: "Erreur pause",
        subtitle: "Impossible d'enregistrer la pause.",
      });
    }
  }

  async function stopActivity(): Promise<void> {
    if (!workerUrl || !state.activeActivity) return;
    try {
      const ev = await buildEvent(
        workerUrl,
        "activity_end",
        state.activeActivity.code,
      );
      await endActivity.mutateAsync(ev);
      pushToast({ variant: "info", title: "Activité arrêtée" });
    } catch {
      pushToast({
        variant: "error",
        title: "Erreur",
        subtitle: "Impossible d'arrêter l'activité.",
      });
    }
  }

  return {
    ...state,
    workerUrl,
    online,
    pendingSync: sessionsOpen.data?.count ?? 0,
    loading: timeline.isLoading,
    badge,
    toggleBreak,
    stopActivity,
  };
}
